/**
 * Public /set-password?token=... — landing page for the magic link sent to
 * bulk-imported customers. The flow:
 *   1. On mount, POST /api/auth/magic-link/verify to greet the user + confirm
 *      the token is still valid.
 *   2. Customer picks a password (min 8 chars, confirm field).
 *   3. POST /api/auth/magic-link/set-password to lock it in.
 *   4. Redirect to /cabinet/login with the email pre-filled.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, Lock, Eye, EyeOff, PawPrint } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useLang } from '@/lib/LangContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const token = params.get('token') || '';

  const [state, setState] = useState('verifying'); // verifying | ready | expired | invalid | saving | done
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);

  const L = (en, ka) => (lang === 'ka' ? ka : en);

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/magic-link/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (res.status === 410) { setState('expired'); return; }
        if (!res.ok) { setState('invalid'); return; }
        const data = await res.json();
        setUserInfo(data);
        setState('ready');
      } catch (_e) {
        setState('invalid');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(L('Password must be at least 8 characters', 'პაროლი უნდა შედგებოდეს მინიმუმ 8 სიმბოლოსგან'));
      return;
    }
    if (password !== confirm) {
      toast.error(L('Passwords do not match', 'პაროლები არ ემთხვევა'));
      return;
    }
    setState('saving');
    try {
      const res = await fetch(`${API}/api/auth/magic-link/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      setState('done');
      toast.success(L('Password set. Signing you in…', 'პაროლი შენახულია. გადამისამართებ…'));
      setTimeout(() => {
        navigate('/cabinet/login', { state: { email: userInfo?.email } });
      }, 1200);
    } catch (e) {
      setState('ready');
      toast.error(e.message || 'Failed');
    }
  };

  // Guard rendering
  if (state === 'verifying') {
    return <Shell><Center><Loader2 className="h-6 w-6 animate-spin text-[#F25C05]" /><p className="mt-3 text-sm text-[#465B70]">{L('Checking your link…', 'ბმულის შემოწმება…')}</p></Center></Shell>;
  }
  if (state === 'invalid') {
    return (
      <Shell><Center>
        <AlertCircle className="h-10 w-10 text-rose-500" />
        <h1 className="mt-3 font-display text-2xl font-bold text-[#05223D]">{L('This link is invalid', 'ეს ბმული არასწორია')}</h1>
        <p className="mt-1 text-sm text-[#465B70]">{L('Please contact SmartPaw to receive a new invitation.', 'გთხოვთ დაუკავშირდით SmartPaw-ს ახალი მოსაწვევის მისაღებად.')}</p>
        <Link to="/" className="mt-4 inline-block text-sm font-bold text-[#0A4D8C] underline-offset-4 hover:underline">{L('Back to home', 'უკან მთავარ გვერდზე')}</Link>
      </Center></Shell>
    );
  }
  if (state === 'expired') {
    return (
      <Shell><Center>
        <AlertCircle className="h-10 w-10 text-amber-500" />
        <h1 className="mt-3 font-display text-2xl font-bold text-[#05223D]">{L('This link has expired', 'ბმულის ვადა ამოიწურა')}</h1>
        <p className="mt-1 text-sm text-[#465B70]">{L('Please contact SmartPaw to receive a fresh invitation.', 'გთხოვთ დაუკავშირდით SmartPaw-ს ახალი მოსაწვევის მისაღებად.')}</p>
      </Center></Shell>
    );
  }
  if (state === 'done') {
    return <Shell><Center><CheckCircle2 className="h-10 w-10 text-emerald-500" /><p className="mt-3 text-sm text-[#465B70]">{L('Redirecting to your cabinet…', 'გადამისამართება…')}</p></Center></Shell>;
  }

  const greeting = userInfo?.name
    ? L(`Welcome, ${userInfo.name}!`, `მოგესალმებით, ${userInfo.name}!`)
    : L('Welcome to SmartPaw!', 'მოგესალმებით SmartPaw-ში!');

  return (
    <Shell>
      <Card className="border-[#0A4D8C1A] bg-[#FDFBF7] shadow-xl">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-2">
            <PawPrint className="h-5 w-5 text-[#F25C05]" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#F25C05]">SmartPaw</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold text-[#05223D]" data-testid="set-password-heading">{greeting}</h1>
          <p className="mt-2 text-sm text-[#465B70] leading-relaxed">
            {L(
              'Your account is ready. Set a password to review the plan we\'ve prepared for your pet and activate your cabinet.',
              'თქვენი ანგარიში მზადაა. შექმენით პაროლი, რომ გადახედოთ თქვენი ცხოველისთვის მომზადებულ გეგმას და აქტიური გახადოთ თქვენი კაბინეტი.',
            )}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="set-password-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#465B70] font-bold">
                {L('Email', 'ელფოსტა')}
              </Label>
              <Input value={userInfo?.email || ''} disabled className="h-10 rounded-xl bg-white mt-1" data-testid="set-password-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#465B70] font-bold">
                {L('New password', 'ახალი პაროლი')}
              </Label>
              <div className="relative mt-1">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  className="h-10 rounded-xl pr-10"
                  data-testid="set-password-new"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7A8A99] hover:text-[#05223D]" aria-label="Toggle password visibility">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="mt-1 text-[10px] text-[#7A8A99]">{L('At least 8 characters', 'მინიმუმ 8 სიმბოლო')}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#465B70] font-bold">
                {L('Confirm password', 'გაიმეორეთ პაროლი')}
              </Label>
              <Input
                type={showPw ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                className="h-10 rounded-xl mt-1"
                data-testid="set-password-confirm"
              />
            </div>

            <Button type="submit" disabled={state === 'saving'} className="w-full h-11 rounded-xl bg-[#F25C05] hover:bg-[#D95204] text-white font-bold" data-testid="set-password-submit">
              {state === 'saving' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
              {L('Set password & continue', 'პაროლის შენახვა და გაგრძელება')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {children}
      </div>
    </div>
  );
}

function Center({ children }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16" data-testid="set-password-status">
      {children}
    </div>
  );
}
